import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowLeft, FaTag, FaEye, FaEyeSlash } from 'react-icons/fa';

// Declare grecaptcha for TypeScript
declare global {
  interface Window {
    grecaptcha: any;
  }
}

const AskQuestion: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    // Load reCAPTCHA Enterprise script
    const loadRecaptcha = () => {
      return new Promise<void>((resolve) => {
        // Check if script already exists
        const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
        if (existingScript) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/enterprise.js?render=6Ldrkb4rAAAAANFfe76ri-Yj2QClnjW5HbSijNQm';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('reCAPTCHA Enterprise script loaded');
          resolve();
        };
        script.onerror = () => {
          console.error('Failed to load reCAPTCHA Enterprise script');
          resolve(); // Still resolve to avoid blocking
        };
        document.head.appendChild(script);
      });
    };

    loadRecaptcha().then(() => {
      // Wait for grecaptcha to be available
      const checkGrecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          console.log('reCAPTCHA Enterprise ready');
          setRecaptchaReady(true);
        } else {
          setTimeout(checkGrecaptcha, 100);
        }
      };
      checkGrecaptcha();
    });

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length < 10) {
      newErrors.title = 'Title must be at least 10 characters';
    } else if (title.length > 150) {
      newErrors.title = 'Title must be less than 150 characters';
    }

    if (!content.trim()) {
      newErrors.content = 'Question content is required';
    } else if (content.length < 20) {
      newErrors.content = 'Question must be at least 20 characters';
    }

    if (tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    } else if (tags.length > 5) {
      newErrors.tags = 'Maximum 5 tags allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      
      if (tag && !tags.includes(tag) && tags.length < 5) {
        setTags([...tags, tag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!recaptchaReady) {
      setErrors({ submit: 'reCAPTCHA is still loading. Please wait a moment and try again.' });
      return;
    }

    setLoading(true);

    try {
      // Execute reCAPTCHA Enterprise
      console.log('Executing reCAPTCHA Enterprise...');
      const token = await window.grecaptcha.enterprise.execute('6Ldrkb4rAAAAANFfe76ri-Yj2QClnjW5HbSijNQm', {
        action: 'ASK_QUESTION'
      });
      console.log('reCAPTCHA token received:', token);

      const response = await fetch('/api/forum/questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          tags,
          recaptchaToken: token
        })
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/forum/question/${data.data._id}`);
      } else {
        const error = await response.json();
        setErrors({ submit: error.message || 'Failed to create question' });
      }
    } catch (error) {
      console.error('Error submitting question:', error);
      if (error instanceof Error && error.message.includes('grecaptcha')) {
        setErrors({ submit: 'reCAPTCHA verification failed. Please refresh the page and try again.' });
      } else {
        setErrors({ submit: 'Network error. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdownPreview = (text: string) => {
    // Simple markdown rendering for preview
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/forum')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Forum
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Ask a Question</h1>
          <p className="text-gray-600 mt-2">
            Share your Farsi learning questions with the community
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's your question? Be specific."
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1">{errors.title}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              {title.length}/150 characters
            </p>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Question Details *
              </label>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {showPreview ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            </div>
            
            {!showPreview ? (
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Provide more context about your question. You can use **bold**, *italic*, and `code` formatting."
                rows={10}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical ${
                  errors.content ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            ) : (
              <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white min-h-[200px]">
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(content) }}
                />
              </div>
            )}
            
            {errors.content && (
              <p className="text-red-600 text-sm mt-1">{errors.content}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              {content.length} characters
            </p>
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
              Tags *
            </label>
            <div className="flex items-center gap-2 mb-2">
              <FaTag className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                id="tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tags (press Enter or comma to add)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {errors.tags && (
              <p className="text-red-600 text-sm mt-1">{errors.tags}</p>
            )}
            <p className="text-gray-500 text-sm">
              Add up to 5 tags to help others find your question. Examples: grammar, vocabulary, pronunciation
            </p>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* reCAPTCHA Enterprise Status */}
          <div className="text-sm text-gray-600 mb-4">
            {recaptchaReady ? (
              <span className="text-green-600">✓ reCAPTCHA Enterprise is ready</span>
            ) : (
              <span className="text-yellow-600">⏳ Loading reCAPTCHA Enterprise...</span>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading || !recaptchaReady}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              {loading ? 'Posting Question...' : 'Post Question'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/forum')}
              className="px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Help Section */}
        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#001f3f] mb-4">Writing a Good Question</h3>
          <ul className="space-y-2 text-blue-800 text-left" dir="ltr">
            <li>• Be specific and clear about what you're asking</li>
            <li>• Provide context about your Farsi learning level</li>
            <li>• Include examples if relevant</li>
            <li>• Use appropriate tags to help others find your question</li>
            <li>• Check if your question has already been answered</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AskQuestion; 